/**
 * Workflow Step Modal Component
 *
 * Purpose: Modal form for creating and editing workflow steps with validation.
 *          Handles step linking (next/previous), status flags, effects, and AI prompt configuration.
 *
 * Architecture: Self-contained modal component with form validation.
 *              Mirrors the FieldModal pattern from metadata modals.
 *
 * Dependencies: React, Material-UI, WorkflowService
 * Author: Development Team
 * Created: 2026-04-13
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Button,
  TextField,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Autocomplete,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Public as GlobalIcon,
  Business as TenantIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import {
  WorkflowDefinition,
  WorkflowStep,
  WorkflowStepFormData,
  WorkflowStepReadRef,
  StepTypeCapability,
  WORKFLOW_STATUS_FLAGS,
  WORKFLOW_STEP_EFFECTS,
} from '../../../services/workflowService';
import { TenantInfo } from '../../../services/metadataService';
import { AIAgent } from '../../../services/aiAgentService';
import { generateStepKey } from '../../../utils/patterns';
import DescriptionField, { descriptionLengthError } from '../../common/DescriptionField';
import { SecondaryButton, DestructiveButton } from '../../common/ActionButtons';
import DialogFooter from '../../common/DialogFooter';
import { renderMultiSelectOption } from '../../common/MultiSelectOption';

const NO_STEP_TYPE = '';

// Component interfaces
export interface WorkflowStepModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  step?: WorkflowStep;
  tenantContext: TenantInfo;
  canEdit: boolean;
  workflowDefinitions: WorkflowDefinition[];
  stepsInWorkflow: WorkflowStep[];
  availableAgents: AIAgent[];
  /** Engine step-type registry from core-api's `GET /metadata/workflow-step-types` (ORCH-286 §5.4/P2). */
  stepTypeOptions: StepTypeCapability[];
  onClose: () => void;
  onSave: (data: WorkflowStepFormData) => Promise<void>;
  onDelete?: (step: WorkflowStep) => Promise<void>;
}

/** Extract a human-readable message from a core-api validation error (ORCH-286 §8).
 * `ValueError`s surface as `{detail: string}`; FastAPI/pydantic 422s surface as
 * `{detail: [{loc, msg, type}, ...]}`. */
function extractSaveErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) =>
        d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : null
      )
      .filter((m): m is string => !!m);
    if (messages.length) return messages.join('; ');
  }
  return error instanceof Error ? error.message : fallback;
}

/**
 * Modal component for create/edit workflow step operations
 */
export const WorkflowStepModal: React.FC<WorkflowStepModalProps> = ({
  open,
  mode,
  step,
  tenantContext,
  canEdit,
  workflowDefinitions,
  stepsInWorkflow = [],
  availableAgents,
  stepTypeOptions,
  onClose,
  onSave,
  onDelete,
}) => {
  // Form data state
  const [formData, setFormData] = useState<WorkflowStepFormData>({
    workflow_id: '',
    tenant_id: tenantContext.tenant_id,
    label: '',
    description: null,
    status_flags: [],
    assigned_agents: [],
    next_steps: [],
    on_set_effects: [],
    on_leave_effects: [],
    step_key: null,
    step_type: null,
    reviewer_agent: null,
    reads: [],
    review_subject_label: null,
    max_review_loops: null,
    summary_heading: null,
    requires_approval: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Initialize form data when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && step) {
        setFormData({
          workflow_id: step.workflow_id,
          tenant_id: step.tenant_id,
          label: step.label,
          description: step.description ?? null,
          status_flags: step.status_flags || [],
          assigned_agents: step.assigned_agents || [],
          next_steps: step.next_steps || [],
          on_set_effects: step.on_set_effects || [],
          on_leave_effects: step.on_leave_effects || [],
          step_key: step.step_key ?? null,
          step_type: step.step_type ?? null,
          reviewer_agent: step.reviewer_agent ?? null,
          reads: step.reads || [],
          review_subject_label: step.review_subject_label ?? null,
          max_review_loops: step.max_review_loops ?? null,
          summary_heading: step.summary_heading ?? null,
          requires_approval: step.requires_approval ?? false,
        });
      } else {
        setFormData({
          workflow_id: '',
          tenant_id: tenantContext.tenant_id,
          label: '',
          description: null,
          status_flags: [],
          assigned_agents: [],
          next_steps: [],
          on_set_effects: [],
          on_leave_effects: [],
          step_key: null,
          step_type: null,
          reviewer_agent: null,
          reads: [],
          review_subject_label: null,
          max_review_loops: null,
          summary_heading: null,
          requires_approval: false,
        });
      }
      setErrors({});
      setSaveError(null);
    }
  }, [open, mode, step, tenantContext]);

  // Auto-generate step_key from label (create mode only) — read-only after create (ORCH-286 §5.3).
  useEffect(() => {
    if (mode !== 'create') return;
    if (formData.label.trim()) {
      const key = generateStepKey(formData.label);
      setFormData((prev) => (prev.step_key === key ? prev : { ...prev, step_key: key }));
    } else if (formData.step_key) {
      setFormData((prev) => ({ ...prev, step_key: null }));
    }
  }, [formData.label, mode, formData.step_key]);

  // Producer/reviewer/reads/review config only makes sense for an engine step
  // (step_type set) — clear it when the user switches back to a business
  // lifecycle step so stale config isn't silently saved.
  const handleStepTypeChange = (nextStepType: string) => {
    setFormData((prev) => ({
      ...prev,
      step_type: nextStepType || null,
      ...(nextStepType
        ? {}
        : {
            reviewer_agent: null,
            reads: [],
            review_subject_label: null,
            max_review_loops: null,
            summary_heading: null,
          }),
    }));
  };

  // Get the stable reference ID for a step (always the GLOBAL UUID).
  // For GLOBAL steps: step_id. For overridden steps: global_step_id.
  const getStepReferenceId = (s: WorkflowStep): string => {
    return s.global_step_id || s.step_id;
  };

  // Filter available steps for next/previous linking (exclude self)
  const availableLinkSteps = stepsInWorkflow.filter((s) => {
    // Exclude the current step being edited (by reference ID)
    if (mode === 'edit' && step) {
      const currentRefId = getStepReferenceId(step);
      const candidateRefId = getStepReferenceId(s);
      if (candidateRefId === currentRefId) return false;
    }
    // Only show steps from the same workflow
    if (formData.workflow_id && s.workflow_id !== formData.workflow_id) return false;
    return true;
  });

  // Steps a `reads` entry can point at — earlier active steps in the same
  // workflow that carry a step_key. Server-side (§8) is the source of truth
  // for "earlier in the walk"; this list is a best-effort narrowing by
  // sequence_order so most invalid picks are caught before save.
  const availableReadSteps = stepsInWorkflow.filter((s) => {
    if (!s.step_key) return false;
    if (formData.workflow_id && s.workflow_id !== formData.workflow_id) return false;
    if (mode === 'edit' && step && getStepReferenceId(s) === getStepReferenceId(step)) return false;
    if (mode === 'edit' && step && s.sequence_order >= step.sequence_order) return false;
    return true;
  });

  const isEngineStep = !!formData.step_type;
  const producerAgents = availableAgents.filter((a) => a.settings?.kind === 'producer');
  const reviewerAgents = availableAgents.filter((a) => a.settings?.kind === 'reviewer');
  // A business lifecycle step (no step_type) keeps the pre-ORCH-286
  // behaviour: any active agent is assignable, informationally (ORCH-199).
  const assignableAgents = isEngineStep ? producerAgents : availableAgents;

  const addReadRef = () => {
    setFormData({ ...formData, reads: [...(formData.reads || []), { step_key: '', label: '' }] });
  };

  const updateReadRef = (index: number, patch: Partial<WorkflowStepReadRef>) => {
    const next = [...(formData.reads || [])];
    next[index] = { ...next[index], ...patch };
    setFormData({ ...formData, reads: next });
  };

  const removeReadRef = (index: number) => {
    const next = [...(formData.reads || [])];
    next.splice(index, 1);
    setFormData({ ...formData, reads: next });
  };

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate workflow definition
    if (!formData.workflow_id.trim()) {
      newErrors.workflow_id = 'Workflow definition is required';
    }

    // Validate label
    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    } else if (formData.label.length > 100) {
      newErrors.label = 'Label must be under 100 characters';
    }

    // Validate description length (optional field, soft cap blocks save)
    const descError = descriptionLengthError(formData.description);
    if (descError) newErrors.description = descError;

    if (formData.step_key && !/^[a-z][a-z0-9_]*$/.test(formData.step_key)) {
      newErrors.step_key =
        'Step Key must start with a lowercase letter and contain only lowercase letters, digits, or underscores';
    }
    if (isEngineStep && !formData.step_key) {
      newErrors.step_key = `Step type '${formData.step_type}' requires a Step Key`;
    }
    if (
      formData.max_review_loops !== null &&
      formData.max_review_loops !== undefined &&
      formData.max_review_loops < 0
    ) {
      newErrors.max_review_loops = 'Max Review Loops must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setSaveError(null);

      const dataToSave: WorkflowStepFormData = {
        ...formData,
        description: formData.description?.trim() || null,
        step_key: formData.step_key?.trim() || null,
        review_subject_label: formData.review_subject_label?.trim() || null,
        summary_heading: formData.summary_heading?.trim() || null,
        reads: (formData.reads || []).filter((r) => r.step_key && r.label.trim()),
      };

      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Failed to save workflow step:', error);
      setSaveError(extractSaveErrorMessage(error, 'Failed to save workflow step'));
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!step || !onDelete) return;

    try {
      setLoading(true);
      await onDelete(step);
      onClose();
    } catch (error) {
      console.error('Failed to delete workflow step:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {tenantContext.is_global ? <GlobalIcon /> : <TenantIcon />}
          {mode === 'create' ? 'Create Workflow Step' : 'Edit Workflow Step'}
          <Chip
            label={tenantContext.name}
            size="small"
            color={tenantContext.is_global ? 'warning' : 'primary'}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          {tenantContext.is_global && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are creating/editing a GLOBAL workflow step. This will affect all tenants.
            </Alert>
          )}

          {!tenantContext.is_global && step?.tenant_id === 'GLOBAL' && mode === 'edit' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You are editing a GLOBAL workflow step. This will create a tenant-specific override
              instead of modifying the GLOBAL step.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Workflow Definition */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={workflowDefinitions}
                getOptionLabel={(option) => option.label}
                value={
                  workflowDefinitions.find((wd) => wd.workflow_id === formData.workflow_id) || null
                }
                onChange={(_event, newValue) => {
                  setFormData({
                    ...formData,
                    workflow_id: newValue?.workflow_id || '',
                    // Clear step links when workflow changes
                    next_steps: [],
                  });
                }}
                disabled={mode === 'edit' || !canEdit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Workflow Definition"
                    required
                    error={!!errors.workflow_id}
                    helperText={errors.workflow_id || 'Select the workflow this step belongs to'}
                    data-testid="step-workflow-input"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Box>{option.label}</Box>
                      <Box
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          fontFamily: 'monospace',
                        }}
                      >
                        {option.workflow_id}
                      </Box>
                    </Box>
                  </Box>
                )}
                data-testid="step-workflow-autocomplete"
              />
            </Grid>

            {/* Label */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Label"
                required
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                error={!!errors.label}
                helperText={errors.label || 'Human-readable label for the step'}
                fullWidth
                disabled={!canEdit}
                data-testid="step-label-input"
              />
            </Grid>

            {/* Step Key */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Step Key"
                value={formData.step_key || ''}
                onChange={(e) => {
                  if (mode === 'create') {
                    setFormData({ ...formData, step_key: e.target.value });
                  }
                }}
                error={!!errors.step_key}
                helperText={
                  mode === 'create'
                    ? errors.step_key || 'Auto-generated from label (editable)'
                    : 'Step Key (read-only — immutable after create)'
                }
                fullWidth
                disabled={mode === 'edit' || !canEdit}
                placeholder={mode === 'create' ? 'competitor_scan' : undefined}
                data-testid="step-key-input"
              />
            </Grid>

            {/* Description */}
            <Grid size={{ xs: 12, md: 6 }}>
              <DescriptionField
                label="Description"
                value={formData.description || ''}
                onChange={(value) => setFormData({ ...formData, description: value || null })}
                error={!!errors.description}
                helperText={
                  errors.description || 'Optional description of what happens in this step'
                }
                fullWidth
                disabled={!canEdit}
                data-testid="step-description-input"
              />
            </Grid>

            {/* Status Flags */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                renderOption={renderMultiSelectOption((option: string) => option)}
                options={[...WORKFLOW_STATUS_FLAGS]}
                value={formData.status_flags || []}
                onChange={(_event, newValue) => {
                  setFormData({ ...formData, status_flags: newValue });
                }}
                disabled={!canEdit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status Flags"
                    helperText="Status flags associated with this step"
                    data-testid="step-status-flags-input"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
              />
            </Grid>

            {/* Step Type */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth disabled={!canEdit}>
                <InputLabel id="step-type-label">Step Type</InputLabel>
                <Select
                  labelId="step-type-label"
                  value={formData.step_type || NO_STEP_TYPE}
                  label="Step Type"
                  onChange={(e) => handleStepTypeChange(e.target.value)}
                  data-testid="step-type-select"
                >
                  <MenuItem value={NO_STEP_TYPE}>
                    <em>None (business lifecycle step)</em>
                  </MenuItem>
                  {stepTypeOptions.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Requires Approval */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.requires_approval}
                    onChange={(e) =>
                      setFormData({ ...formData, requires_approval: e.target.checked })
                    }
                    disabled={!canEdit}
                    data-testid="step-requires-approval-switch"
                  />
                }
                label="Requires Sign-off"
              />
            </Grid>

            {mode === 'edit' && !!step?.engine_phases?.length && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  Engine Phases (derived, read-only)
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {step.engine_phases!.map((phase) => (
                    <Chip key={phase} label={phase} size="small" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Assigned Agents */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={assignableAgents}
                getOptionLabel={(option) => option.label}
                value={assignableAgents.filter((a) =>
                  (formData.assigned_agents || []).includes(a.ai_agent_id)
                )}
                onChange={(_event, newValue) => {
                  setFormData({
                    ...formData,
                    assigned_agents: newValue.map((a) => a.ai_agent_id),
                  });
                }}
                disabled={!canEdit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isEngineStep ? 'Producer Agents' : 'Assigned Agents'}
                    helperText={
                      isEngineStep
                        ? 'Producer agents that execute when this step is active'
                        : 'AI agents assigned to execute when this step is active'
                    }
                    data-testid="step-assigned-agents-input"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.label}
                      size="small"
                      {...getTagProps({ index })}
                      key={option.ai_agent_id}
                    />
                  ))
                }
                renderOption={renderMultiSelectOption((option: { label: string }) => option.label)}
                data-testid="step-assigned-agents-autocomplete"
              />
            </Grid>

            {isEngineStep && (
              <>
                {/* Reviewer Agent */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={reviewerAgents}
                    getOptionLabel={(option) => option.label}
                    value={
                      reviewerAgents.find((a) => a.ai_agent_id === formData.reviewer_agent) || null
                    }
                    onChange={(_event, newValue) => {
                      setFormData({ ...formData, reviewer_agent: newValue?.ai_agent_id || null });
                    }}
                    disabled={!canEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Reviewer Agent"
                        helperText="Reviewer that grades this step's output and drives the review loop"
                        data-testid="step-reviewer-agent-input"
                      />
                    )}
                    data-testid="step-reviewer-agent-autocomplete"
                  />
                </Grid>

                {/* Review Subject Label */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Review Subject Label"
                    value={formData.review_subject_label || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, review_subject_label: e.target.value || null })
                    }
                    helperText="Label the reviewer sees its subject under"
                    fullWidth
                    disabled={!canEdit}
                    data-testid="step-review-subject-label-input"
                  />
                </Grid>

                {/* Max Review Loops */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Max Review Loops"
                    type="number"
                    value={formData.max_review_loops ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        max_review_loops: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    error={!!errors.max_review_loops}
                    helperText={
                      errors.max_review_loops || 'Defaults to the engine review-retry cap'
                    }
                    slotProps={{ htmlInput: { min: 0 } }}
                    fullWidth
                    disabled={!canEdit}
                    data-testid="step-max-review-loops-input"
                  />
                </Grid>

                {/* Summary Heading */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Summary Heading"
                    value={formData.summary_heading || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, summary_heading: e.target.value || null })
                    }
                    helperText="Heading for this step in the run summary; defaults to the label"
                    fullWidth
                    disabled={!canEdit}
                    data-testid="step-summary-heading-input"
                  />
                </Grid>

                {/* Reads From */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Reads From
                  </Typography>
                  {(formData.reads || []).map((entry, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}
                      data-testid={`step-reads-row-${index}`}
                    >
                      <Autocomplete
                        sx={{ flex: 1 }}
                        options={availableReadSteps}
                        getOptionLabel={(option) => option.label}
                        value={
                          availableReadSteps.find((s) => s.step_key === entry.step_key) || null
                        }
                        onChange={(_event, newValue) =>
                          updateReadRef(index, { step_key: newValue?.step_key || '' })
                        }
                        disabled={!canEdit}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Earlier Step"
                            data-testid={`step-reads-step-input-${index}`}
                          />
                        )}
                      />
                      <TextField
                        sx={{ flex: 1 }}
                        label="Context Label"
                        value={entry.label}
                        onChange={(e) => updateReadRef(index, { label: e.target.value })}
                        disabled={!canEdit}
                        placeholder="e.g. Approved brief output"
                        data-testid={`step-reads-label-input-${index}`}
                      />
                      <IconButton
                        onClick={() => removeReadRef(index)}
                        disabled={!canEdit}
                        aria-label="Remove read"
                        data-testid={`step-reads-remove-${index}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    onClick={addReadRef}
                    disabled={!canEdit}
                    startIcon={<AddIcon />}
                    size="small"
                    data-testid="step-reads-add-button"
                  >
                    Add Read
                  </Button>
                </Grid>
              </>
            )}

            {/* Next Steps */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={availableLinkSteps}
                getOptionLabel={(option) => option.label}
                value={availableLinkSteps.filter((s) =>
                  (formData.next_steps || []).includes(getStepReferenceId(s))
                )}
                onChange={(_event, newValue) => {
                  setFormData({
                    ...formData,
                    next_steps: newValue.map((s) => getStepReferenceId(s)),
                  });
                }}
                disabled={!canEdit || !formData.workflow_id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Next Steps"
                    helperText="Steps that can follow this one (multiple selections create a branching workflow)"
                    data-testid="step-next-steps-input"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.label}
                      size="small"
                      {...getTagProps({ index })}
                      key={getStepReferenceId(option)}
                    />
                  ))
                }
                renderOption={renderMultiSelectOption(
                  (option: { label: string; sequence_order?: number | null }) => (
                    <Box>
                      <Box>{option.label}</Box>
                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Order: {option.sequence_order}
                      </Box>
                    </Box>
                  )
                )}
              />
            </Grid>

            {/* On Set Effects */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                renderOption={renderMultiSelectOption((option: string) => option)}
                options={[...WORKFLOW_STEP_EFFECTS]}
                value={formData.on_set_effects || []}
                onChange={(_event, newValue) => {
                  setFormData({ ...formData, on_set_effects: newValue });
                }}
                disabled={!canEdit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="On Set Effects"
                    helperText="Effects triggered when entering this step"
                    data-testid="step-on-set-effects-input"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
              />
            </Grid>

            {/* On Leave Effects */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                renderOption={renderMultiSelectOption((option: string) => option)}
                options={[...WORKFLOW_STEP_EFFECTS]}
                value={formData.on_leave_effects || []}
                onChange={(_event, newValue) => {
                  setFormData({ ...formData, on_leave_effects: newValue });
                }}
                disabled={!canEdit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="On Leave Effects"
                    helperText="Effects triggered when leaving this step"
                    data-testid="step-on-leave-effects-input"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
              />
            </Grid>
          </Grid>
        </>
      </DialogContent>

      <DialogFooter
        destructiveAction={
          mode === 'edit' &&
          onDelete &&
          canEdit && (
            <DestructiveButton
              onClick={handleDelete}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Delete
            </DestructiveButton>
          )
        }
      >
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>

        {canEdit && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};

export default WorkflowStepModal;
